import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePromo } from "./providers/Init";
import { Link } from "react-router-dom";

const Restaurant = React.memo(({ restraunt }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const promo = usePromo();
  const percentage = (100 - promo * 100) || 50;

  // State to check if the div is in view
  const [isVisible, setIsVisible] = useState(false);
  const divRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.3 } // Load images when 30% of the div is in view
    );

    if (divRef.current) {
      observer.observe(divRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Link to={`/restraunt/${restraunt.id}`} className="relative flex flex-col w-full overflow-hidden shadow-lg rounded-xl" ref={divRef}>
      <div className="absolute top-2 left-2">
        <div className="w-16 h-16 rounded-lg bg-gray-300" 
             style={isVisible ? { backgroundImage: `url(${restraunt.logo})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
        </div>
      </div>
      <div className="h-32 w-full bg-gray-200" 
           style={isVisible ? { backgroundImage: `url(${restraunt.cover_photo})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
      </div>
      <div className="flex flex-col p-4 bg-white gap-y-2">
        <div className="flex justify-between w-full">
          <span className="text-lg font-semibold">{restraunt[`name_${currentLanguage}`]}</span>
          <div className="flex items-center gap-x-2">
            <span className="flex items-center text-sm font-semibold">
              <svg className="mr-1" stroke="#FFEA00" fill="#FFEA00" strokeWidth="0" viewBox="0 0 1024 1024" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                <path d="M908.1 353.1l-253.9-36.9L540.7 86.1c-3.1-6.3-8.2-11.4-14.5-14.5-15.8-7.8-35-1.3-42.9 14.5L369.8 316.2l-253.9 36.9c-7 1-13.4 4.3-18.3 9.3a32.05 32.05 0 0 0 .6 45.3l183.7 179.1-43.4 252.9a31.95 31.95 0 0 0 46.4 33.7L512 754l227.1 119.4c6.2 3.3 13.4 4.4 20.3 3.2 17.4-3 29.1-19.5 26.1-36.9l-43.4-252.9 183.7-179.1c5-4.9 8.3-11.3 9.3-18.3 2.7-17.5-9.5-33.7-27-36.3z"></path>
              </svg>
              {restraunt.average_rating}
            </span>
            <span className="text-xs">({restraunt.rate_count})</span>
          </div>
        </div>
        <div className="flex items-center text-sm gap-x-3 text-neutral-600">
          {restraunt[currentLanguage].kitchens.map((kitchen, index) => (
            <span key={index}>{kitchen}</span>
          ))}
        </div>
        <div className="flex items-center w-full gap-x-2">
          <div className="flex items-center p-1 text-xs font-medium rounded-full gap-x-1 bg-neutral-50 w-fit">
            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 1H9v2h6V1zm-4 13h2V8h-2v6zm8.03-6.61l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42C16.07 4.74 14.12 4 12 4c-4.97 0-9 4.03-9 9s4.02 9 9 9 9-4.03 9-9c0-2.12-.74-4.07-1.97-5.61zM12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"></path>
            </svg>
            <span>{t("deliveryTime")}</span>
          </div>
          <span>|</span>
          <div className="flex items-center text-xs gap-x-1">
            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 1024 1024" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
              <path d="M380 704h264c4.4 0 8-3.6 8-8v-84c0-4.4-3.6-8-8-8h-40c-4.4 0-8 3.6-8 8v36H428v-36c0-4.4-3.6-8-8-8h-40c-4.4 0-8 3.6-8 8v84c0 4.4 3.6 8 8 8zm340-123a40 40 0 1 0 80 0 40 40 0 1 0-80 0zm239-167.6L935.3 372a8 8 0 0 0-10.9-2.9l-50.7 29.6-78.3-216.2a63.9 63.9 0 0 0-60.9-44.4H301.2c-34.7 0-65.5 22.4-76.2 55.5l-74.6 205.2-50.8-29.6a8 8 0 0 0-10.9 2.9L65 413.4c-2.2 3.8-.9 8.6 2.9 10.8l60.4 35.2-14.5 40c-1.2 3.2-1.8 6.6-1.8 10v348.2c0 15.7 11.8 28.4 26.3 28.4h67.6c12.3 0 23-9.3 25.6-22.3l7.7-37.7h545.6l7.7 37.7c2.7 13 13.3 22.3 25.6 22.3h67.6c14.5 0 26.3-12.7 26.3-28.4V509.4c0-3.4-.6-6.8-1.8-10l-14.5-40 60.3-35.2a8 8 0 0 0 3-10.8z"></path>
            </svg>
            <span className="font-medium line-through">{t("originalPrice")}</span>
          </div>
        </div>
      </div>
    </Link>
  );
});

export default Restaurant;
